import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getWorkspaceFeedback } from "api/services/workspace-service";
import { ITrainingFeedbackModel } from "api/models/response/feedback/ITrainingFeedbackModel";
import { useAuthContext } from "hooks/useAuthContext";
import { WORKSPACE_FEEDBACK_QUERY_KEY } from "hooks/feedback/queryKeys";
import { RewardType } from "models/RewardType";

const toFeedbackByRewardType = (feedbacks: ITrainingFeedbackModel[]) => {
    const feedbackByRewardType: Partial<Record<RewardType, ITrainingFeedbackModel>> = {};

    for (const feedback of feedbacks) {
        const existing = feedbackByRewardType[feedback.rewardType];
        if (!existing || new Date(feedback.updatedAt).getTime() > new Date(existing.updatedAt).getTime()) {
            feedbackByRewardType[feedback.rewardType] = feedback;
        }
    }

    return feedbackByRewardType;
};

export const useGetWorkspaceFeedback = () => {
    const { userState } = useAuthContext();
    const workspaceId = userState.user.selectedWorkspace.workspaceId;

    const query = useQuery({
        queryKey: [...WORKSPACE_FEEDBACK_QUERY_KEY, workspaceId],
        queryFn: async (): Promise<ITrainingFeedbackModel[]> => {
            if (!workspaceId) return [];

            const response = await getWorkspaceFeedback({ workspaceId });
            if (!response.success) {
                throw new Error(response.message ?? "feedbackManagement.loadError");
            }

            return response.data ?? [];
        },
        enabled: !!workspaceId,
    });

    const feedbacks = query.data ?? [];
    const feedbackByRewardType = useMemo(() => toFeedbackByRewardType(feedbacks), [feedbacks]);

    return {
        feedbacks,
        feedbackByRewardType,
        feedbackLoading: query.isLoading || query.isFetching,
        feedbackRefetching: query.isFetching && !query.isLoading,
        refetch: query.refetch,
    };
};
